import {
  type AdminBranding,
  BRANDING_DISPLAY_NAME_MAX,
  CSRF_HEADER,
  type LogoInvalidReason,
  type LogoRecord,
  type LogoVariant,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, CircleDashed, ImageUp, type LucideIcon } from "lucide-react";
import { type ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { t as translate } from "../i18n/i18n.ts";
import { useMe } from "../me.ts";
import { useSession } from "../session.tsx";
import { SaveState, SettingText } from "../settings-shell/controls.tsx";
import { SettingRowFrame } from "../settings-shell/SettingRowFrame.tsx";
import {
  SettingsGroupPage,
  SettingsSectionBlock,
  SettingsShell,
} from "../settings-shell/SettingsShell.tsx";
import { SETTING_ROW_CLASS } from "../settings-shell/setting-anchors.ts";
import { useSettingSave } from "../settings-shell/use-setting-save.ts";
import { type AccentOutcome, accentOutcome, THEME_SURFACES } from "../theme/tenant.ts";
import { useAdminApi } from "./admin-api.tsx";
import { adminRefusal } from "./entity-tree.ts";

/**
 * The Branding screen (openspec change `tenant-branding`, design D7): the tenant's display name,
 * an accent per theme with what it will do stated in words, and a logo per theme previewed at the
 * shell's size on both surfaces. Tenant admins edit; entity admins and operators under a support
 * grant read (under a grant no logo image is loaded: its type and dimensions show instead).
 */

const THEMES = ["light", "dark"] as const;
type Theme = (typeof THEMES)[number];
const HEX = /^#[0-9a-fA-F]{6}$/;
const ACCENT_KEY = { light: "branding.accentLight", dark: "branding.accentDark" } as const;
const BRANDING_QUERY = ["admin", "branding"] as const;

/** Saves invalidate the record, the settings catalogue and `me`, so the shell updates too. */
function useRefresh() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: BRANDING_QUERY }),
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] }),
      queryClient.invalidateQueries({ queryKey: ["me"] }),
    ]);
}

export function BrandingScreen() {
  const { t } = useTranslation();
  const { admin, readOnly } = useAdminApi();
  const { roles } = useSession();
  const editable = !readOnly && roles.includes("tenant_admin");
  const record = useQuery({
    queryKey: BRANDING_QUERY,
    queryFn: async (): Promise<AdminBranding> => ok(await admin.branding.$get()),
    retry: false,
  });
  return (
    <div className="flex flex-col gap-6">
      <SettingsShell
        title={t("branding.title")}
        lead={t("branding.lead")}
        notices={
          editable ? null : (
            <Notice>{readOnly ? t("branding.readOnlySupport") : t("branding.readOnly")}</Notice>
          )
        }
      >
        {record.isPending ? (
          <Skeleton className="h-96 w-full" />
        ) : record.isError ? (
          <Notice tone="danger">{adminRefusal(record.error)}</Notice>
        ) : (
          <BrandingSections record={record.data} editable={editable} images={!readOnly} />
        )}
      </SettingsShell>
    </div>
  );
}

function BrandingSections(props: { record: AdminBranding; editable: boolean; images: boolean }) {
  const { t } = useTranslation();
  const { record, editable, images } = props;
  // Operators under a grant are not members: `me` is the user's own scope only.
  const me = useMe({ enabled: images });
  const heading = useRef<HTMLHeadingElement>(null);
  const label = record.displayName ?? (images ? (me.data?.tenantName ?? "") : "");
  return (
    <SettingsGroupPage
      id="branding"
      heading={t("branding.scope.heading")}
      headingRef={heading}
      purpose={t("branding.scope.purpose")}
    >
      <SettingsSectionBlock id="branding-name" heading={t("branding.name.heading")}>
        <NameRow value={record.displayName} fallback={label} editable={editable} />
      </SettingsSectionBlock>
      <SettingsSectionBlock id="branding-accent" heading={t("branding.accent.heading")}>
        {THEMES.map((theme) => (
          <AccentRow
            key={theme}
            theme={theme}
            value={theme === "light" ? record.accentLight : record.accentDark}
            // The dark theme uses the light accent when it has none of its own.
            inherited={theme === "dark" ? record.accentLight : null}
            editable={editable}
          />
        ))}
      </SettingsSectionBlock>
      <SettingsSectionBlock id="branding-logo" heading={t("branding.logo.heading")}>
        {THEMES.map((theme) => (
          <LogoRow
            key={theme}
            variant={theme}
            logo={theme === "light" ? record.logoLight : record.logoDark}
            light={record.logoLight}
            label={label}
            editable={editable}
            images={images}
          />
        ))}
      </SettingsSectionBlock>
    </SettingsGroupPage>
  );
}

/** A row's anatomy, as every settings row: label and help, the value and its actions, then more. */
function Row(props: {
  id: string;
  label: string;
  help: ReactNode;
  value: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <li className={SETTING_ROW_CLASS}>
      <div className="flex flex-col gap-1.5 @xl:flex-row @xl:items-start @xl:justify-between @xl:gap-6">
        <div className="flex min-w-0 flex-col gap-0.5 @xl:flex-1">
          <span id={`${props.id}-label`} className="text-sm font-medium text-fg">
            {props.label}
          </span>
          <span className="text-sm text-fg-secondary">{props.help}</span>
        </div>
        <span className="flex flex-wrap items-center gap-1 @xl:max-w-[55%] @xl:justify-end">
          {props.value}
          {props.actions}
        </span>
      </div>
      {props.children}
    </li>
  );
}

/** Writes one branding key (`null`: back to the default, by removing it). */
function useBrandingWrite(key: string) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  return async (value: string | null) => {
    if (value === null) {
      const res = await admin.settings[":key"].$delete({ param: { key } }, headers);
      if (!res.ok) {
        throw await apiError(res);
      }
      return;
    }
    await ok(
      await admin.settings[":key"].$put(
        { param: { key }, json: { value, locked: false } },
        headers,
      ),
    );
  };
}

/** The settings write of one branding key, as a mutation that refreshes what shows it. */
function useSettingWrite(key: string) {
  const write = useBrandingWrite(key);
  const refresh = useRefresh();
  return useMutation({ mutationFn: write, onSettled: refresh });
}

function Muted(props: { children: ReactNode }) {
  return <span className="text-sm text-fg-secondary">{props.children}</span>;
}

/**
 * The display name, edited in place (openspec change settings-typed-controls): Enter or leaving
 * the field saves, Escape restores, an empty field goes back to the tenant's name.
 */
function NameRow(props: { value: string | null; fallback: string; editable: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const refresh = useRefresh();
  const key = "branding.displayName";
  const write = useBrandingWrite(key);
  const save = useSettingSave({
    run: (action) => write(action.type === "delete" ? null : (action.value as string | null)),
    settle: refresh,
  });
  const refusal = save.error
    ? save.error instanceof ApiError && save.error.status === 400
      ? t("branding.name.invalid")
      : adminRefusal(save.error)
    : null;
  const pending = save.pending?.type === "put" ? (save.pending.value as string | null) : undefined;
  const shown = pending !== undefined ? pending : props.value;
  return (
    <SettingRowFrame
      settingKey={key}
      labelId={`${id}-label`}
      sourceId={`${id}-source`}
      label={t("branding.name.label")}
      help={
        props.fallback && !props.value
          ? t("branding.name.helpFallback", { name: props.fallback })
          : t("branding.name.help")
      }
      control={
        props.editable ? (
          <>
            <SaveState state={save.state} />
            <SettingText
              labelledBy={`${id}-label`}
              busy={save.busy}
              value={shown}
              placeholder={props.fallback || t("branding.default")}
              max={BRANDING_DISPLAY_NAME_MAX}
              invalidText={t("branding.name.invalid")}
              onEdit={save.clear}
              onCommit={(value) => save.save({ type: "put", value, locked: false })}
            />
          </>
        ) : props.value ? (
          <span className="text-sm font-medium text-fg">{props.value}</span>
        ) : (
          <Muted>{t("branding.default")}</Muted>
        )
      }
      marks={[]}
      below={refusal ? <Notice tone="danger">{refusal}</Notice> : null}
    />
  );
}

function EditActions(props: { disabled: boolean; pending: boolean; onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="submit" size="sm" disabled={props.disabled || props.pending}>
        {props.pending ? t("settings.saving") : t("settings.save")}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={props.onCancel}>
        {t("settings.cancel")}
      </Button>
    </div>
  );
}

const OUTCOME_ICON: Record<AccentOutcome, LucideIcon> = {
  applied: CircleCheck,
  "fills-only": CircleAlert,
  default: CircleDashed,
};

/** What the accent will do in its theme, in words and with an icon (never colour alone). */
function OutcomeLine(props: { outcome: AccentOutcome; theme: Theme; live?: boolean }) {
  const { t } = useTranslation();
  const Icon = OUTCOME_ICON[props.outcome];
  return (
    <p
      className="flex items-start gap-1.5 text-xs text-fg-secondary"
      {...(props.live ? { role: "status", "aria-live": "polite" } : {})}
    >
      <Icon
        aria-hidden="true"
        className={
          props.outcome === "fills-only"
            ? "mt-px size-3.5 shrink-0 text-warning-fg"
            : "mt-px size-3.5 shrink-0 text-fg-muted"
        }
        strokeWidth={1.75}
      />
      <span>
        {t(`branding.accent.outcome.${props.outcome}`, {
          theme: t(`branding.themeInline.${props.theme}`),
        })}
      </span>
    </p>
  );
}

function Swatch(props: { color: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-4 shrink-0 rounded-sm border border-line"
      style={{ backgroundColor: props.color }}
    />
  );
}

function AccentRow(props: {
  theme: Theme;
  value: string | null;
  inherited: string | null;
  editable: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const write = useSettingWrite(ACCENT_KEY[props.theme]);
  const [draft, setDraft] = useState<string | null>(null);
  const effective = props.value ?? props.inherited;
  const valid = draft !== null && HEX.test(draft);
  const label = t(`branding.accent.label.${props.theme}`);
  const refusal = write.error
    ? write.error instanceof ApiError && write.error.status === 400
      ? t("branding.accent.invalid")
      : adminRefusal(write.error)
    : null;
  return (
    <Row
      id={id}
      label={label}
      help={
        props.theme === "dark" && !props.value && props.inherited
          ? t("branding.accent.helpDarkInherited")
          : t("branding.accent.help")
      }
      value={
        props.value ? (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-fg tabular-nums">
            <Swatch color={props.value} />
            {props.value.toLowerCase()}
          </span>
        ) : props.inherited ? (
          <span className="inline-flex items-center gap-2 text-sm text-fg-secondary tabular-nums">
            <Swatch color={props.inherited} />
            {t("branding.accent.sameAsLight")}
          </span>
        ) : (
          <Muted>{t("branding.default")}</Muted>
        )
      }
      actions={
        props.editable && draft === null ? (
          <>
            <Button
              size="xs"
              variant="ghost"
              aria-label={`${t("settings.behaviour.change")}: ${label}`}
              onClick={() => {
                write.reset();
                setDraft(effective ?? "#");
              }}
            >
              {t("settings.behaviour.change")}
            </Button>
            {props.value ? (
              <Button
                size="xs"
                variant="ghost"
                aria-label={`${t("branding.remove")}: ${label}`}
                disabled={write.isPending}
                onClick={() => write.mutate(null)}
              >
                {t("branding.remove")}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {draft === null ? (
        <OutcomeLine outcome={accentOutcome(effective, props.theme)} theme={props.theme} />
      ) : null}
      {draft === null && refusal ? <Notice tone="danger">{refusal}</Notice> : null}
      {props.editable && draft !== null ? (
        <form
          className="flex flex-col gap-3"
          aria-labelledby={`${id}-label`}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !write.isPending) {
              write.mutate(draft, { onSuccess: () => setDraft(null) });
            }
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="color"
              aria-label={t("branding.accent.picker", {
                theme: t(`branding.themeInline.${props.theme}`),
              })}
              value={valid ? draft.toLowerCase() : "#000000"}
              className="h-9 w-12 cursor-pointer rounded-md border border-line bg-surface-raised p-1"
              onChange={(e) => {
                write.reset();
                setDraft(e.target.value);
              }}
            />
            <Input
              aria-label={t("branding.accent.hex", {
                theme: t(`branding.themeInline.${props.theme}`),
              })}
              aria-invalid={!valid}
              aria-describedby={`${id}-outcome`}
              value={draft}
              maxLength={7}
              spellCheck={false}
              autoComplete="off"
              className="w-32 font-mono tabular-nums"
              onChange={(e) => {
                write.reset();
                setDraft(e.target.value.trim());
              }}
            />
          </div>
          <div id={`${id}-outcome`}>
            {valid ? (
              <OutcomeLine outcome={accentOutcome(draft, props.theme)} theme={props.theme} live />
            ) : (
              <p className="text-xs text-fg-muted">{t("branding.accent.format")}</p>
            )}
          </div>
          {refusal ? <Notice tone="danger">{refusal}</Notice> : null}
          <EditActions
            disabled={!valid || draft.toLowerCase() === props.value?.toLowerCase()}
            pending={write.isPending}
            onCancel={() => setDraft(null)}
          />
        </form>
      ) : null}
    </Row>
  );
}

/** The refusal of an upload, in the person's language. */
function uploadRefusal(error: Error): string {
  if (error instanceof ApiError) {
    if (error.status === 415) {
      return translate("branding.logo.refused.type");
    }
    if (error.status === 413 || error.code === "upload_size_limit") {
      return translate("branding.logo.refused.size");
    }
    if (error.code === "logo_invalid") {
      const reason = (error.details?.reason ?? "malformed") as LogoInvalidReason;
      return translate(`branding.logo.refused.${reason}`);
    }
  }
  return adminRefusal(error);
}

const formatSize = (bytes: number, language: string) =>
  bytes >= 1024 * 1024
    ? `${new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`
    : `${new Intl.NumberFormat(language, { maximumFractionDigits: 0 }).format(Math.max(1, bytes / 1024))} KB`;

/** A logo at the shell's size (24px tall, 160px wide at most) on one theme's base surface. */
function PreviewTile(props: { theme: Theme; url: string | null; label: string }) {
  const { t } = useTranslation();
  return (
    <figure className="flex min-w-0 flex-col gap-1">
      <div
        className="flex h-12 w-48 items-center rounded-md border border-line px-3"
        style={{ backgroundColor: THEME_SURFACES[props.theme] }}
      >
        {props.url ? (
          <img
            src={props.url}
            alt={props.label}
            className="block h-6 max-w-40 object-contain object-left"
          />
        ) : null}
      </div>
      <figcaption className="text-xs text-fg-muted">
        {t(`branding.logo.on.${props.theme}`)}
      </figcaption>
    </figure>
  );
}

function LogoRow(props: {
  variant: LogoVariant;
  logo: LogoRecord | null;
  /** The light logo, which the dark theme shows when it has none of its own. */
  light: LogoRecord | null;
  label: string;
  editable: boolean;
  images: boolean;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const refresh = useRefresh();
  const input = useRef<HTMLInputElement>(null);
  const label = t(`branding.logo.label.${props.variant}`);
  const headers = { [CSRF_HEADER]: csrfToken };
  const upload = useMutation({
    mutationFn: async (file: File) =>
      ok(
        await admin.branding.logo[":variant"].$put(
          { param: { variant: props.variant } },
          {
            headers: { ...headers, "content-type": file.type || "application/octet-stream" },
            init: { body: file },
          },
        ),
      ),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await admin.branding.logo[":variant"].$delete(
        { param: { variant: props.variant } },
        { headers },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSettled: refresh,
  });
  const error = upload.error ?? remove.error;
  const shown = props.logo ?? (props.variant === "dark" ? props.light : null);
  // The shell shows a dark logo only alongside a light one (brand-logo-held): a dark logo is
  // offered once a light one exists, and a dark one left without it is said to be unused.
  const needsLight = props.variant === "dark" && !props.light;
  const meta = props.logo
    ? [
        props.logo.contentType === "image/png" ? "PNG" : "WebP",
        `${props.logo.width} × ${props.logo.height} px`,
        formatSize(props.logo.sizeBytes, i18n.language),
      ].join(" · ")
    : null;
  return (
    <Row
      id={id}
      label={label}
      help={
        props.variant === "light" ? (
          <>
            {t("branding.logo.help.light")}
            {/* Emails carry the light logo only (account-emails): say what they can't show. */}
            <span className="mt-0.5 block">{t("branding.logo.emails")}</span>
          </>
        ) : (
          t("branding.logo.help.dark")
        )
      }
      value={
        props.logo ? (
          <span className="text-sm font-medium text-fg tabular-nums">{meta}</span>
        ) : props.variant === "dark" && props.light ? (
          <Muted>{t("branding.logo.usesLight")}</Muted>
        ) : needsLight ? (
          <Muted>{t("branding.logo.darkNeedsLight")}</Muted>
        ) : (
          <Muted>{t("branding.logo.none")}</Muted>
        )
      }
      actions={
        props.editable ? (
          <>
            <input
              ref={input}
              type="file"
              accept="image/png,image/webp,.png,.webp"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) {
                  remove.reset();
                  upload.mutate(file);
                }
              }}
            />
            {needsLight ? null : (
              <Button
                size="xs"
                variant="ghost"
                aria-label={`${props.logo ? t("branding.logo.replace") : t("branding.logo.upload")}: ${label}`}
                disabled={upload.isPending}
                onClick={() => input.current?.click()}
              >
                <ImageUp aria-hidden="true" />
                {upload.isPending
                  ? t("branding.logo.uploading")
                  : props.logo
                    ? t("branding.logo.replace")
                    : t("branding.logo.upload")}
              </Button>
            )}
            {props.logo ? (
              <Button
                size="xs"
                variant="ghost"
                aria-label={`${t("branding.remove")}: ${label}`}
                disabled={remove.isPending}
                onClick={() => {
                  upload.reset();
                  remove.mutate();
                }}
              >
                {t("branding.remove")}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {props.images ? (
        <div className="flex flex-wrap gap-3">
          {THEMES.map((theme) => (
            <PreviewTile
              key={theme}
              theme={theme}
              url={shown?.url ?? null}
              label={props.label || t("branding.logo.unnamed")}
            />
          ))}
        </div>
      ) : null}
      {props.variant === "dark" && props.logo && !props.light ? (
        <Notice tone="warning">{t("branding.logo.darkUnused")}</Notice>
      ) : null}
      {error ? <Notice tone="danger">{uploadRefusal(error)}</Notice> : null}
    </Row>
  );
}
